import { useState, useEffect, useCallback, useRef } from 'react';
import { PDFDocument } from 'pdf-lib';
import { Document, Page as PDFPage } from 'react-pdf';
import {
  Modal,
  Button,
  Group,
  Text,
  Grid,
  Checkbox,
  Badge,
  Stack,
  ActionIcon,
  Tooltip,
  Alert,
} from '@mantine/core';
import {
  IconEye,
  IconEyeOff,
  IconCheck,
  IconX,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classes from './pdfPageSelector.module.css';
import '@/config/pdf'; // Initialize PDF.js worker

// Individual page component to avoid Document wrapper issues
const PDFPagePreview = ({ file, pageNumber, className }) => {
  console.log(`PDFPagePreview: Rendering page ${pageNumber} with file:`, file);

  return (
    <Document
      file={file}
      loading={<div className={className}>טוען...</div>}
      error={<div className={className}>שגיאה בהצגה</div>}
      onLoadSuccess={(pdf) => {
        console.log(`Document loaded successfully for page ${pageNumber}, total pages: ${pdf.numPages}`);
      }}
      onLoadError={(error) => {
        console.error(`Document load error for page ${pageNumber}:`, error);
      }}
    >
      <PDFPage
        pageNumber={pageNumber}
        width={200}
        height={280}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        className={className}
        loading={<div className={className}>טוען עמוד...</div>}
        error={<div className={className}>שגיאה בהצגה</div>}
        onLoadSuccess={() => {
          console.log(`Page ${pageNumber} loaded successfully`);
        }}
        onLoadError={(error) => {
          console.error(`Page ${pageNumber} load error:`, error);
        }}
      />
    </Document>
  );
};

PDFPagePreview.propTypes = {
  file: PropTypes.object.isRequired,
  pageNumber: PropTypes.number.isRequired,
  className: PropTypes.string,
};

const PDFPageSelector = ({
  opened,
  onClose,
  file,
  onPagesSelected,
  initialSelectedPages = [],
}) => {
  const { t } = useTranslation();
  const [pdfPages, setPdfPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);
  const [pdfData, setPdfData] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const pdfDataRef = useRef(null);

  const loadPDFPages = useCallback(async () => {
    if (!file) {
      return;
    }

    console.log('Starting PDF load with file:', {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    setLoading(true);
    setPdfError(null);
    setPdfLoadError(false);
    try {
      // Clean up previous URL if it exists
      if (pdfDataRef.current) {
        URL.revokeObjectURL(pdfDataRef.current);
      }

      // Create file URL for react-pdf
      const fileUrl = URL.createObjectURL(file);
      console.log('Created file URL:', fileUrl);
      pdfDataRef.current = fileUrl;
      setPdfData(fileUrl);

      // Also load with pdf-lib for page count
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pageCount = pdfDoc.getPageCount();

      console.log(`PDF loaded successfully: ${pageCount} pages`);

      // Create page data structure
      const pages = Array.from({ length: pageCount }, (_, index) => ({
        pageNumber: index + 1,
        selected: false,
      }));

      setPdfPages(pages);
      // Use initial selected pages if provided (for editing), otherwise start with no selection
      setSelectedPages(initialSelectedPages.length > 0 ? initialSelectedPages : []);
    } catch (error) {
      console.error('PDF loading error:', error);
      setPdfError(`Failed to load PDF: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [file, initialSelectedPages]);

  useEffect(() => {
    if (opened && file) {
      loadPDFPages();
    }
  }, [opened, file, loadPDFPages]);

  useEffect(() => {
    // Cleanup file URL when component unmounts
    return () => {
      if (pdfDataRef.current) {
        URL.revokeObjectURL(pdfDataRef.current);
      }
    };
  }, []);

  const handlePageToggle = (pageNumber) => {
    setSelectedPages((prev) => {
      const isCurrentlySelected = prev.includes(pageNumber);
      let newSelection;

      if (isCurrentlySelected) {
        // Deselecting - allow deselecting any page
        newSelection = prev.filter((p) => p !== pageNumber);
      } else {
        // Selecting - check if we already have 15 pages selected
        if (prev.length >= 15) {
          return prev; // Don't allow selecting more than 15 pages
        }
        newSelection = [...prev, pageNumber].sort((a, b) => a - b);
      }

      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const maxSelectable = Math.min(pdfPages.length, 15);

    if (selectedPages.length === maxSelectable) {
      // Deselect all pages
      setSelectedPages([]);
    } else {
      // Select all - but limit to maximum 15 pages
      const newSelection = pdfPages.slice(0, maxSelectable).map((page) => page.pageNumber);
      setSelectedPages(newSelection);
    }
  };

  const handleConfirm = async () => {
    if (selectedPages.length === 0) {
      return;
    }

    try {
      setLoading(true);

      // Load the original PDF
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      // Create a new PDF with only selected pages
      const newPdfDoc = await PDFDocument.create();

      // Copy selected pages to new PDF
      const pageIndices = selectedPages.map((pageNumber) => pageNumber - 1);
      const copiedPagesArrays = await newPdfDoc.copyPages(pdfDoc, pageIndices);
      copiedPagesArrays.forEach((copiedPage) => newPdfDoc.addPage(copiedPage));

      // Generate the new PDF file
      const pdfBytes = await newPdfDoc.save();
      const newFile = new File([pdfBytes], file.name, { type: 'application/pdf' });

      onPagesSelected(newFile, selectedPages);
      onClose(false); // false = not cancelled, confirmed
    } catch (error) {
      setPdfError(`Error creating PDF: ${error.message}`);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Pass cancelled=true to parent component
    onClose(true);
  };

  const getSelectAllButtonText = () => {
    const maxSelectable = Math.min(pdfPages.length, 15);
    if (selectedPages.length === maxSelectable) {
      return t('components.pdfPageSelector.deselectAll');
    }
    if (selectedPages.length === 0) {
      return t('components.pdfPageSelector.selectAll');
    }
    return t('components.pdfPageSelector.selectMore');
  };

  const renderPagesContent = () => {
    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <Text>{t('components.pdfPageSelector.loading')}</Text>
        </div>
      );
    }
    if (pdfError) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('components.pdfPageSelector.error.title')}
          color="red"
        >
          {pdfError}
        </Alert>
      );
    }
    return (
      <>
        {pdfLoadError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title={t('components.pdfPageSelector.previewUnavailable.title')}
            color="yellow"
            mb="md"
          >
            {t('components.pdfPageSelector.previewUnavailable.message')}
          </Alert>
        )}
        <Grid className={classes.pageGrid}>
          {pdfPages.map((page) => {
            const isSelected = selectedPages.includes(page.pageNumber);
            const canSelect = selectedPages.length < 15;
            const isDisabled = !isSelected && !canSelect;

            return (
              <Grid.Col
                key={page.pageNumber}
                span={{
                  base: 12,
                  sm: 6,
                  md: 4,
                  lg: 3,
                }}
              >
                <div
                  className={`${classes.pageCard} ${isSelected ? classes.selected : ''
                    } ${isDisabled ? classes.disabled : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isDisabled) {
                      handlePageToggle(page.pageNumber);
                    }
                  }}
                >
                  <div className={classes.pagePreview}>
                    {previewMode && pdfData && !pdfLoadError ? (
                      <PDFPagePreview
                        file={pdfData}
                        pageNumber={page.pageNumber}
                        className={classes.pdfPage}
                      />
                    ) : (
                      <div className={classes.pageNumber}>
                        <Text size="xl" fw={700}>
                          {page.pageNumber}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {t('components.pdfPageSelector.page')}
                        </Text>
                      </div>
                    )}
                  </div>

                  <div className={classes.pageInfo}>
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isDisabled) {
                          handlePageToggle(page.pageNumber);
                        }
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      label={`${t('components.pdfPageSelector.page')} ${page.pageNumber}`}
                      size="sm"
                    />
                  </div>
                </div>
              </Grid.Col>
            );
          })}
        </Grid>
      </>
    );
  };

  if (!opened || !file) {
    return null;
  }

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={
        <Group gap="xs">
          <Text size="lg" fw={600}>
            {t('components.pdfPageSelector.title')}
          </Text>
          <Badge color="blue" variant="light">
            {pdfPages.length} {t('components.pdfPageSelector.pages')}
          </Badge>
        </Group>
      }
      size="90%"
      centered
      styles={{
        content: { maxHeight: '90vh' },
      }}
    >
      <Stack gap="md">
        {/* Header Actions */}
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            <strong>{t('components.pdfPageSelector.section.title')}</strong>
            <ul>
              <li>{t('components.pdfPageSelector.section.list.first')}</li>
              <li>{t('components.pdfPageSelector.section.list.second')}</li>
            </ul>
          </Text>
          <Text size="sm" c="dimmed">
            {selectedPages.length} {t('components.pdfPageSelector.selected')} / {pdfPages.length} {t('components.pdfPageSelector.total')}
            <br />
            {t('components.pdfPageSelector.selectionLimits')}
          </Text>
        </Group>

        {/* Pages Grid */}
        {renderPagesContent()}

        {/* Footer Actions */}
        <Group justify="flex-end" mt="md">
          <Button
            variant="light"
            onClick={handleCancel}
            disabled={loading}
          >
            <IconX size={16} />
            {t('globals.back')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedPages.length === 0 || loading}
            loading={loading}
            color="blue"
            className={selectedPages.length === 0 ? classes.disabledButton : ''}
          >
            <IconCheck size={16} />
            {t('globals.continue')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

PDFPageSelector.propTypes = {
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  file: PropTypes.object,
  onPagesSelected: PropTypes.func.isRequired,
  initialSelectedPages: PropTypes.arrayOf(PropTypes.number),
};

export default PDFPageSelector;
